/**
 * Shared chart layout — ALL acoustic plots (mobile Skia, Expo Canvas2D,
 * future web dock charts) must size themselves through this module.
 *
 * Never invent width as `windowWidth - N`. Nested padding (scroll + card)
 * makes that guess wrong and clips/overflows the plot. Measure the host
 * container, then call `createPlotFrame(measuredWidth, height)`.
 *
 * Interactive charts: map gestures with `viewToPlot` / `plotToLogFreq` /
 * `plotToLinear` so scrubbers stay aligned with the same pad/plot box
 * the renderer uses.
 */

export interface ChartPad {
  t: number;
  r: number;
  b: number;
  l: number;
}

/** Default inset for axis labels + stroke clearance. Keep in sync across renderers. */
export const DEFAULT_CHART_PAD: ChartPad = { t: 14, r: 12, b: 34, l: 38 };

export const FREQ_RESPONSE_PAD: ChartPad = { t: 16, r: 12, b: 34, l: 38 };
export const RT60_PAD: ChartPad = { t: 12, r: 12, b: 30, l: 38 };

/**
 * Pixel geometry of a chart view.
 * - `width`/`height` — full host (canvas / Skia view)
 * - `plot*` — drawable data area inside the pad
 */
export interface PlotFrame {
  width: number;
  height: number;
  pad: ChartPad;
  plotX: number;
  plotY: number;
  plotW: number;
  plotH: number;
}

export function createPlotFrame(
  width: number,
  height: number,
  pad: ChartPad = DEFAULT_CHART_PAD,
): PlotFrame {
  const w = Math.max(0, Math.floor(width));
  const h = Math.max(0, Math.floor(height));
  const plotW = Math.max(0, w - pad.l - pad.r);
  const plotH = Math.max(0, h - pad.t - pad.b);
  return {
    width: w,
    height: h,
    pad,
    plotX: pad.l,
    plotY: pad.t,
    plotW,
    plotH,
  };
}

/** True when the plot box fits entirely inside the host (no negative size). */
export function plotFrameFits(frame: PlotFrame): boolean {
  return (
    frame.plotW > 0
    && frame.plotH > 0
    && frame.plotX + frame.plotW <= frame.width + 1e-6
    && frame.plotY + frame.plotH <= frame.height + 1e-6
  );
}

/** Convert a point in the chart view (origin top-left of host) into plot-local coords. */
export function viewToPlot(
  frame: PlotFrame,
  viewX: number,
  viewY: number,
): { x: number; y: number } {
  return { x: viewX - frame.plotX, y: viewY - frame.plotY };
}

/** Clamp a plot-local point into the drawable area (for scrubbing). */
export function clampPlotPoint(
  frame: PlotFrame,
  x: number,
  y: number,
): { x: number; y: number } {
  return {
    x: Math.min(frame.plotW, Math.max(0, x)),
    y: Math.min(frame.plotH, Math.max(0, y)),
  };
}
